import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { displayTimeFromSeconds, EMAIL_REGEX, normalizeRecipientEmails } from '../../utilities/common';
import { setLeavePrompt } from '../manage-users/usersSlice';
import { config } from '../../utilities/config';
import LoadingButton from '../../components/LoadingButton';
import { getSmartBenchmarkSectorsApi } from '../smart-benchmark/smartBenchmarkApi';

const createSchema = yup.object().shape({
  orgWlbId: yup.number().required('This field is a required field'),
  optLocale: yup.string().required('This field is a required field'),
  orgName: yup.string().max(128).required('This field is a required field'),
  orgSectorId: yup.number().nullable(),
  usrEmail: yup.string().required('This field is a required field').email('This field is must be a valid email address'),
  orgSmartBenchmarkRecipients: yup.array()
    .of(yup.string().trim().matches(EMAIL_REGEX, 'Each recipient must be a valid email address'))
    .test(
      'unique-smart-benchmark-recipients',
      'Recipient email addresses must be unique',
      (value = []) => normalizeRecipientEmails(value).length === value.length
    )
});

const OrganizationForm = ({
  isResendEmail,
  resendDelayCount,
  onResendEmail,
  onSubmit,
  onCancel,
  initialValues = {},
  buttonText = 'Create',
  isEditing = false,
  disabled,
}) => {
  const [recipientInputValue, setRecipientInputValue] = useState('');
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty }
  } = useForm({
    defaultValues: {
      ...initialValues,
      optLocale: initialValues?.optLocale || config.DEFAULT_LOCALE,
      orgSmartBenchmarkRecipients: normalizeRecipientEmails(initialValues?.orgSmartBenchmarkRecipients || [])
    },
    resolver: yupResolver(createSchema),
    mode: 'onChange'
  });
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.users.user);
  const orgWlbId = useWatch({
    control,
    name: 'orgWlbId'
  });
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setLeavePrompt(isDirty));
  }, [isDirty, dispatch]);

  const [sectors, setSectors] = useState([]);
  useEffect(() => {
    const loadSectors = async () => {
      const res = await getSmartBenchmarkSectorsApi(userInfo.usrOrgId);
      setSectors(res);
    };
    loadSectors();
  }, [userInfo.usrOrgId]);

  const appendRecipientEmails = (inputValue, currentValues, onChange) => {
    const nextEmails = normalizeRecipientEmails([
      ...(currentValues || []),
      ...String(inputValue || '')
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean)
    ]);

    onChange(nextEmails);
  };

  const onSubmitHandler = async (data) => {
    try {
      dispatch(setLeavePrompt(false));
      await onSubmit({
        ...data,
        orgSmartBenchmarkRecipients: normalizeRecipientEmails(data.orgSmartBenchmarkRecipients || [])
      });
    } catch (e) {
      dispatch(setLeavePrompt(isDirty));
    }
  };

  const onCancelHandler = () => onCancel();
  const whitelabels = useSelector((state) => state.whitelabels.whitelabels);
  const getWhitelabelOptions = () => {
    if (!whitelabels) return null;
    return whitelabels.map((wl) => {
      return (
        <option
          key={wl.wlbId}
          value={wl.wlbId}
          defaultValue={wl.wlbId === orgWlbId}
        >
          {wl.name}
        </option>
      );
    });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmitHandler)}>
      <Row>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>Organization Name *</Form.Label>
            <Form.Control {...register('orgName')} isInvalid={errors.orgName} />
            <Form.Control.Feedback type="invalid">{errors.orgName?.message}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>&nbsp;</Form.Label>
            {onResendEmail &&
              <div className="form-control resend-email-container">
                <Button disabled={!isResendEmail || resendDelayCount} variant="info" onClick={onResendEmail}>
                  {isResendEmail === 0 ? <Spinner animation="grow" size="sm" /> : 'Resend activation email'}
                </Button>
                {isResendEmail && resendDelayCount ? <p><em>(You can retry in {displayTimeFromSeconds(resendDelayCount)} minutes)</em></p> : null}
              </div>
            }
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>Description </Form.Label>
            <Form.Control as="textarea" rows="5" style={{ resize: 'none' }} {...register('orgDesc')} />
          </Form.Group>
        </Col>
        <Col></Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>Whitelabel *</Form.Label>
            <Form.Control {...register('orgWlbId')} as="select">
              <>
                {!isEditing ? (
                  <option key={-1} defaultValue="" hidden>
                    Select Whitelabel
                  </option>
                ) : (<></>)}
                {getWhitelabelOptions()}
              </>
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.orgWlbId?.message}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>{t('Language')} *</Form.Label>
            <Controller
              name="optLocale"
              control={control}
              render={({ field }) => {
                const format = (d) => field.onChange(field.value = d.value);
                const localeOptions = userInfo.localeOptions || [];
                return (
                  <Select
                    value={localeOptions.find((a) => field.value === a.value)}
                    options={localeOptions}
                    defaultValue={localeOptions.find((a) => initialValues.optLocale === a.value)}
                    onChange={format}
                    placeholder=""
                  />
                );
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>Email of admin *</Form.Label>
            <Form.Control {...register('usrEmail')} readOnly={isEditing} isInvalid={errors.usrEmail} />
            <Form.Control.Feedback type="invalid">{errors.usrEmail?.message}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>First Name of admin</Form.Label>
            <Form.Control {...register('usrFirstname')} readOnly={isEditing} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>Last Name of admin</Form.Label>
            <Form.Control {...register('usrLastname')} readOnly={isEditing} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>{t('Sector')}</Form.Label>

            <Controller
              name="orgSectorId"
              control={control}
              render={({ field }) => {
                const options = (sectors || []).map((sector) => ({
                  value: sector.sectorId,
                  label: sector.sectorName
                }));
                return (
                  <Select
                    value={options.find((s) => s.value === field.value) || null}
                    options={options}
                    onChange={(d) => field.onChange(d?.value ?? null)}
                    placeholder={t('Select Sector')}
                    isClearable
                    menuPortalTarget={document.body}
                  />
                );
              }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>{t('Smart Benchmark Recipients')}</Form.Label>

            <Controller
              name="orgSmartBenchmarkRecipients"
              control={control}
              render={({ field }) => {
                const recipientOptions = (field.value || []).map((email) => ({
                  value: email,
                  label: email
                }));

                return (
                  <>
                    <CreatableSelect
                      isMulti
                      menuIsOpen={false}
                      components={{ DropdownIndicator: null }}
                      value={recipientOptions}
                      inputValue={recipientInputValue}
                      onInputChange={(value, meta) => {
                        if (meta.action === 'input-change') {
                          setRecipientInputValue(value);
                        }
                      }}
                      onChange={(options) => {
                        field.onChange(
                          normalizeRecipientEmails((options || []).map((option) => option.value))
                        );
                      }}
                      onCreateOption={(value) => {
                        appendRecipientEmails(value, field.value, field.onChange);
                        setRecipientInputValue('');
                      }}
                      onKeyDown={(event) => {
                        if (!recipientInputValue.trim()) {
                          return;
                        }

                        if (event.key === 'Enter' || event.key === ',') {
                          event.preventDefault();
                          appendRecipientEmails(recipientInputValue, field.value, field.onChange);
                          setRecipientInputValue('');
                        }
                      }}
                      onBlur={() => {
                        if (recipientInputValue.trim()) {
                          appendRecipientEmails(recipientInputValue, field.value, field.onChange);
                          setRecipientInputValue('');
                        }

                        field.onBlur();
                      }}
                      placeholder={t('Enter email addresses')}
                      formatCreateLabel={(value) => t('Add "{{value}}"', { value })}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          borderColor: errors.orgSmartBenchmarkRecipients ? '#dc3545' : base.borderColor,
                          boxShadow: errors.orgSmartBenchmarkRecipients
                            ? '0 0 0 0.2rem rgba(220,53,69,.25)'
                            : base.boxShadow
                        })
                      }}
                    />
                    <Form.Text className="text-muted">
                      {t('Enter one or more email addresses separated by commas.')}
                    </Form.Text>
                    {errors.orgSmartBenchmarkRecipients && (
                      <div className="invalid-feedback d-block">
                        {errors.orgSmartBenchmarkRecipients.message}
                      </div>
                    )}
                  </>
                );
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Button className="mt-3 mb-5" type="submit" disabled={disabled || (!isValid || !isDirty)}>
        {buttonText}
      </Button>
      <Button variant="secondary" className="ml-4 mt-3 mb-5" onClick={onCancelHandler}>
        {t('Cancel')}
      </Button>
      <LoadingButton isLoading={disabled} className="ml-4 mt-3 mb-5" />
    </Form>
  );
};

export default OrganizationForm;
